/**
 * One item (#180): its story at the top, its recipes as tabs, House first.
 *
 * Each tab is a recipe, drawn by `SpecView` over the recipe's spec view, so
 * the exploded build, the editor, the price and the versions are the ones
 * the Recipes screen always had. What this page owns is the item itself —
 * name, kind, story, style, tags — and the family: adding a variant, and
 * making one the House recipe.
 */

import { useMemo, useState } from "react";
import { HOUSE_LABEL, houseRecipe, normaliseTags, recipeRank, specView, variantLabel, type Item, type ItemKind, type Recipe, type Spec } from "@baros/core";
import { api, ApiError } from "../api.js";
import { useApp } from "../context.js";
import { hasFeature } from "../features.js";
import { STATUS_LABEL, statusColour } from "../format.js";
import { SpecView } from "../SpecView.js";
import "./items.css";

interface Props {
  readonly item: Item;
  readonly recipes: readonly Recipe[];
  /** The recipe to open on; the House one when absent. */
  readonly recipeId?: string | undefined;
  readonly startEditing: boolean;
  readonly onBack: () => void;
  readonly onOpenRecipe: (recipeId: string, edit?: boolean) => void;
}

type ItemDraft = Pick<Item, "name" | "kind" | "style" | "tags" | "creator" | "added" | "history">;

const KIND_LABEL: Readonly<Record<ItemKind, string>> = { drink: "Drink", food: "Food" };

export function ItemView(p: Props) {
  const app = useApp();
  const house = houseRecipe(p.recipes, p.item.itemId);
  // House, then live variants, then what is being tested, then retired —
  // `recipeRank`, the same order the service deck swipes in.
  const tabs = useMemo(() => [...p.recipes].sort((a, b) => recipeRank(a) - recipeRank(b) || a.label.localeCompare(b.label)), [p.recipes]);
  const current = tabs.find((r) => r.recipeId === p.recipeId) ?? house ?? tabs[0];
  const [showRetired, setShowRetired] = useState(false);
  const [editingItem, setEditingItem] = useState(false);
  const [draft, setDraft] = useState<ItemDraft>(() => ({ ...p.item }));
  const [adding, setAdding] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const canEdit = app.me.can["spec:edit"];
  const retiredCount = tabs.filter((r) => r.status === "retired" && !r.house).length;
  const shownTabs = tabs.filter((r) => showRetired || r.status !== "retired" || r.house || r.recipeId === current?.recipeId);

  const spec: Spec | undefined = current ? specView(p.item, current, house?.recipeId) : undefined;

  async function saveItem() {
    setBusy(true);
    setMsg(null);
    try {
      await api.saveItem(app.token, p.item.itemId, { ...draft, tags: normaliseTags(draft.tags), style: draft.style.map((s) => s.trim()).filter(Boolean) });
      await app.refreshLibrary();
      setEditingItem(false);
      setMsg({ kind: "ok", text: "Item saved." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not save the item" });
    } finally {
      setBusy(false);
    }
  }

  async function addRecipe(label: string) {
    setBusy(true);
    setMsg(null);
    try {
      const { recipe } = await api.createRecipe(app.token, p.item.itemId, label);
      await app.refreshLibrary();
      setAdding(null);
      p.onOpenRecipe(recipe.recipeId, true);
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof ApiError ? err.message : "Could not add the recipe" });
    } finally {
      setBusy(false);
    }
  }

  async function promote(recipe: Recipe) {
    if (!window.confirm(`Make "${recipe.label}" the House ${p.item.name}? The current House recipe is retired, and stays in the history.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.promoteRecipe(app.token, recipe.recipeId);
      await app.refreshLibrary();
      setMsg({ kind: "ok", text: `${p.item.name} is now made to the ${recipe.label} recipe.` });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not promote the recipe" });
    } finally {
      setBusy(false);
    }
  }

  const facts = [p.item.creator ? `by ${p.item.creator}` : "", p.item.added ? `added ${p.item.added}` : ""].filter(Boolean).join(" · ");

  return (
    <div className="screen item-screen">
      <div className="item-head">
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <button className="btn" onClick={p.onBack}>← All items</button>
          <span className="label tag-pill">{KIND_LABEL[p.item.kind]}</span>
          {p.item.style.map((s) => <span key={s} className="label">{s}</span>)}
          {p.item.tags.map((t) => <span key={t} className="label tag-pill">{t}</span>)}
          <span className="spacer" />
          {canEdit && !editingItem && (
            <button className="btn" onClick={() => { setDraft({ ...p.item }); setEditingItem(true); setMsg(null); }}>Edit item</button>
          )}
        </div>
        {editingItem ? (
          <div className="item-editor">
            <div className="grid2">
              <label className="field"><span>Name</span><input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
              <label className="field"><span>Kind</span>
                <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as ItemKind })}>
                  <option value="drink">Drink</option>
                  <option value="food">Food</option>
                </select>
              </label>
              <label className="field"><span>Style (comma separated)</span><input type="text" value={draft.style.join(", ")} onChange={(e) => setDraft({ ...draft, style: e.target.value.split(",").map((s) => s.trim()) })} /></label>
              <label className="field"><span>Tags (comma separated)</span><input type="text" value={draft.tags.join(", ")} onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((t) => t.trim()) })} /></label>
              <label className="field"><span>Created by</span><input type="text" value={draft.creator} onChange={(e) => setDraft({ ...draft, creator: e.target.value })} /></label>
              <label className="field"><span>Added</span><input type="date" value={draft.added ?? ""} onChange={(e) => setDraft({ ...draft, added: e.target.value || null })} /></label>
            </div>
            <label className="field"><span>History and inspiration</span><textarea value={draft.history} onChange={(e) => setDraft({ ...draft, history: e.target.value })} /></label>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn" onClick={() => setEditingItem(false)} disabled={busy}>Cancel</button>
              <button className="btn primary" style={{ height: 44 }} onClick={() => void saveItem()} disabled={busy || !draft.name.trim()}>Save item</button>
            </div>
          </div>
        ) : (
          <>
            <div className="item-name">{p.item.name}</div>
            {(p.item.history || facts) && (
              <div className="item-story">
                {p.item.history && <span>{p.item.history}</span>}
                {facts && <span className="label" style={{ display: "block", marginTop: 4 }}>{facts}</span>}
              </div>
            )}
          </>
        )}
        {msg && <div className={`msg ${msg.kind}`} style={{ marginTop: 10 }}>{msg.text}</div>}

        <div className="item-tabs" role="tablist" aria-label="Recipes">
          {shownTabs.map((r) => (
            <button
              key={r.recipeId}
              role="tab"
              className="btn item-tab"
              aria-selected={r.recipeId === current?.recipeId}
              onClick={() => p.onOpenRecipe(r.recipeId)}
            >
              <span className="dot" style={{ background: statusColour(r.status) }} />
              {r.house ? HOUSE_LABEL : r.label || "Variant"}
              {!r.house && r.status !== "available" && <span className="label" style={{ marginLeft: 6 }}>{STATUS_LABEL[r.status]}</span>}
            </button>
          ))}
          {retiredCount > 0 && !showRetired && (
            <button className="btn" style={{ color: "var(--t2)" }} onClick={() => setShowRetired(true)}>
              + {retiredCount} retired
            </button>
          )}
          {canEdit && adding === null && (
            <button className="btn dashed" onClick={() => { setAdding(""); setMsg(null); }}>+ Recipe</button>
          )}
          {adding !== null && (
            <form
              className="row item-add"
              onSubmit={(e) => {
                e.preventDefault();
                if (adding.trim()) void addRecipe(adding.trim());
              }}
            >
              <input type="text" autoFocus required placeholder="Rum, Mezcal, Non-alc…" value={adding} onChange={(e) => setAdding(e.target.value)} />
              <button className="btn sm" type="submit" disabled={busy}>Add</button>
              <button className="btn sm" type="button" onClick={() => setAdding(null)}>Cancel</button>
              <span className="label" style={{ flexBasis: "100%" }}>Starts as a copy of the House recipe, to edit.</span>
            </form>
          )}
          {canEdit && current && !current.house && current.status !== "retired" && (
            <>
              <span className="spacer" />
              <button className="btn" disabled={busy} onClick={() => void promote(current)}>Make this the House recipe</button>
            </>
          )}
        </div>
      </div>

      {spec && current && (
        <SpecView
          key={`${current.recipeId}-${current.version}`}
          spec={spec}
          seed={p.startEditing ? app.draftBuild ?? undefined : undefined}
          startEditing={p.startEditing}
          library={app.library}
          override={app.overrides.get(current.recipeId)}
          canEdit={canEdit}
          canPrice={app.me.can["price:edit"]}
          canShare={app.me.can["spec:share"] && hasFeature(app.me, "sharing")}
          canBatch={hasFeature(app.me, "batching")}
          placeName={[app.venueState?.venue.name, app.venueState?.venue.suburb].filter(Boolean).join(", ") || undefined}
          token={app.token}
          onBack={p.onBack}
          onPrev={undefined}
          onNext={undefined}
          withinItem={{
            house: current.house,
            houseLines: house && !current.house ? house.lines : null,
            ownTags: current.tags ?? [],
          }}
          onSave={async (draft) => {
            // The editor's name for a variant is its label; the item's own
            // fields are edited on this page, not in the recipe.
            const { spec: saved } = await api.saveRecipe(app.token, current.recipeId, {
              label: current.house ? HOUSE_LABEL : variantLabel(p.item.name, draft.name) || draft.name,
              status: draft.status,
              // This recipe's own tags; the item's are saved above (#188).
              tags: draft.tags,
              lines: draft.lines,
              method: draft.method,
              glass: draft.glass,
              ice: draft.ice,
              garnish: draft.garnish,
              notes: draft.notes,
              sundries: draft.sundries,
              sheetGp: draft.sheetGp,
              talk: draft.talk ?? null,
              version: draft.version,
            });
            app.replaceSpec(saved);
            app.setDraftBuild(null);
            await app.refreshLibrary();
            if (p.startEditing) p.onOpenRecipe(current.recipeId);
            return saved;
          }}
          onSavePrice={async (price, available) => {
            if (!app.venueId) return;
            await api.saveOverride(app.token, app.venueId, current.recipeId, { price, available });
            await app.refreshVenue();
          }}
          onRevise={hasFeature(app.me, "chat") && app.me.can["chat:use"] ? () => app.go({ kind: "chat", params: { specId: current.recipeId } }) : undefined}
          onOpen={(specId) => p.onOpenRecipe(specId)}
          onOpenBatch={hasFeature(app.me, "prep") ? (ingredientId) => app.go({ kind: "prep", params: { tab: "batches", batch: ingredientId } }) : undefined}
          onNewVariant={async (name) => {
            await addRecipe(name);
          }}
        />
      )}
    </div>
  );
}
